/**
 * Static option lists for dropdown questions whose options were not exported
 * from the original quiz platform into data/source.csv.
 *
 * Keyed by question ID. Used in QuestionPage to replace the empty option
 * lists from the quiz manifest with real, searchable choices.
 *
 * IDs use a stable `<prefix>-<slug>` pattern safe to store in the DB.
 * Free-typed answers that don't match any option are stored with the prefix
 * `__free__:` by SearchableDropdown / MultiDropdownChips.
 */

export interface DropdownOption {
  id: string;
  label: string;
}

// ─── School majors ────────────────────────────────────────────────────────────
// Top ~100 US college majors by enrollment popularity.
const SCHOOL_MAJORS: DropdownOption[] = [
  { id: 'major-business-admin',   label: 'Business Administration' },
  { id: 'major-nursing',          label: 'Nursing' },
  { id: 'major-psychology',       label: 'Psychology' },
  { id: 'major-biology',          label: 'Biology' },
  { id: 'major-cs',               label: 'Computer Science' },
  { id: 'major-education',        label: 'Education (General)' },
  { id: 'major-accounting',       label: 'Accounting' },
  { id: 'major-communications',   label: 'Communications' },
  { id: 'major-marketing',        label: 'Marketing' },
  { id: 'major-political-sci',    label: 'Political Science' },
  { id: 'major-finance',          label: 'Finance' },
  { id: 'major-sociology',        label: 'Sociology' },
  { id: 'major-english',          label: 'English / Literature' },
  { id: 'major-economics',        label: 'Economics' },
  { id: 'major-criminal-justice', label: 'Criminal Justice' },
  { id: 'major-mech-eng',         label: 'Mechanical Engineering' },
  { id: 'major-history',          label: 'History' },
  { id: 'major-elem-ed',          label: 'Elementary Education' },
  { id: 'major-cis',              label: 'Computer Information Systems' },
  { id: 'major-kinesiology',      label: 'Kinesiology / Exercise Science' },
  { id: 'major-health-sci',       label: 'Health Sciences' },
  { id: 'major-public-health',    label: 'Public Health' },
  { id: 'major-premed',           label: 'Pre-Medicine / Pre-Med' },
  { id: 'major-elec-eng',         label: 'Electrical Engineering' },
  { id: 'major-chemistry',        label: 'Chemistry' },
  { id: 'major-social-work',      label: 'Social Work' },
  { id: 'major-graphic-design',   label: 'Graphic Design' },
  { id: 'major-it',               label: 'Information Technology' },
  { id: 'major-intl-business',    label: 'International Business' },
  { id: 'major-hr',               label: 'Human Resources' },
  { id: 'major-mis',              label: 'Management Information Systems' },
  { id: 'major-civil-eng',        label: 'Civil Engineering' },
  { id: 'major-prelaw',           label: 'Pre-Law / Legal Studies' },
  { id: 'major-philosophy',       label: 'Philosophy' },
  { id: 'major-fine-arts',        label: 'Art / Fine Arts' },
  { id: 'major-architecture',     label: 'Architecture' },
  { id: 'major-theater',          label: 'Theater / Drama' },
  { id: 'major-music',            label: 'Music' },
  { id: 'major-film',             label: 'Film / Cinema Studies' },
  { id: 'major-journalism',       label: 'Journalism' },
  { id: 'major-pr',               label: 'Public Relations' },
  { id: 'major-religion',         label: 'Religious Studies / Theology' },
  { id: 'major-anthropology',     label: 'Anthropology' },
  { id: 'major-statistics',       label: 'Statistics' },
  { id: 'major-mathematics',      label: 'Mathematics' },
  { id: 'major-physics',          label: 'Physics' },
  { id: 'major-linguistics',      label: 'Linguistics' },
  { id: 'major-foreign-lang',     label: 'Foreign Languages' },
  { id: 'major-env-sci',          label: 'Environmental Science' },
  { id: 'major-nutrition',        label: 'Nutrition / Dietetics' },
  { id: 'major-pharmacy',         label: 'Pharmacy / Pre-Pharmacy' },
  { id: 'major-public-admin',     label: 'Public Administration' },
  { id: 'major-industrial-eng',   label: 'Industrial Engineering' },
  { id: 'major-chem-eng',         label: 'Chemical Engineering' },
  { id: 'major-aerospace',        label: 'Aerospace / Aeronautical Engineering' },
  { id: 'major-software-eng',     label: 'Software Engineering' },
  { id: 'major-data-sci',         label: 'Data Science' },
  { id: 'major-biochem',          label: 'Biochemistry' },
  { id: 'major-neuro',            label: 'Neuroscience' },
  { id: 'major-prenursing',       label: 'Pre-Nursing' },
  { id: 'major-early-ed',         label: 'Early Childhood Education' },
  { id: 'major-special-ed',       label: 'Special Education' },
  { id: 'major-sports-mgmt',      label: 'Sports Management' },
  { id: 'major-real-estate',      label: 'Real Estate' },
  { id: 'major-supply-chain',     label: 'Supply Chain / Logistics' },
  { id: 'major-interior-design',  label: 'Interior Design' },
  { id: 'major-photography',      label: 'Photography' },
  { id: 'major-fashion',          label: 'Fashion Design' },
  { id: 'major-construction',     label: 'Construction Management' },
  { id: 'major-hospitality',      label: 'Hotel / Hospitality Management' },
  { id: 'major-tourism',          label: 'Tourism / Travel Management' },
  { id: 'major-culinary',         label: 'Culinary Arts' },
  { id: 'major-dental',           label: 'Dental Hygiene / Pre-Dentistry' },
  { id: 'major-pt',               label: 'Physical Therapy / Pre-PT' },
  { id: 'major-ot',               label: 'Occupational Therapy' },
  { id: 'major-biomed-eng',       label: 'Biomedical Engineering' },
  { id: 'major-prevetinary',      label: 'Pre-Veterinary / Veterinary Science' },
  { id: 'major-agriculture',      label: 'Agricultural Studies' },
  { id: 'major-gis',              label: 'Geographic Information Systems (GIS)' },
  { id: 'major-cybersecurity',    label: 'Cybersecurity / Information Security' },
  { id: 'major-game-design',      label: 'Game Design / Interactive Media' },
  { id: 'major-media-studies',    label: 'Media Studies' },
  { id: 'major-advertising',      label: 'Advertising' },
  { id: 'major-dance',            label: 'Dance' },
  { id: 'major-museum',           label: 'Museum Studies' },
  { id: 'major-library',          label: 'Library Science' },
  { id: 'major-urban-planning',   label: 'Urban Planning' },
  { id: 'major-intl-relations',   label: 'International Relations' },
  { id: 'major-ethnic-studies',   label: 'Ethnic / Cultural Studies' },
  { id: 'major-gender-studies',   label: 'Gender Studies' },
  { id: 'major-env-eng',          label: 'Environmental Engineering' },
  { id: 'major-marine-sci',       label: 'Oceanography / Marine Science' },
  { id: 'major-geology',          label: 'Geology / Earth Science' },
  { id: 'major-forensics',        label: 'Forensic Science' },
  { id: 'major-actuarial',        label: 'Actuarial Science' },
  { id: 'major-speech',           label: 'Speech / Language Pathology' },
  { id: 'major-radiology',        label: 'Radiologic Technology' },
  { id: 'major-emergency-mgmt',   label: 'Emergency Management' },
  { id: 'major-healthcare-admin', label: 'Healthcare Administration' },
  { id: 'major-other',            label: 'Other / Undeclared' },
];

// ─── Job functions / department areas ─────────────────────────────────────────
const JOB_FUNCTIONS: DropdownOption[] = [
  { id: 'func-software-eng',  label: 'Software Engineering' },
  { id: 'func-data',          label: 'Data Science & Analytics' },
  { id: 'func-product',       label: 'Product Management' },
  { id: 'func-design-ux',     label: 'Design / UX' },
  { id: 'func-sales',         label: 'Sales' },
  { id: 'func-marketing',     label: 'Marketing' },
  { id: 'func-operations',    label: 'Operations' },
  { id: 'func-finance',       label: 'Finance & Accounting' },
  { id: 'func-hr',            label: 'Human Resources' },
  { id: 'func-cs',            label: 'Customer Success' },
  { id: 'func-biz-dev',       label: 'Business Development' },
  { id: 'func-strategy',      label: 'Strategy & Consulting' },
  { id: 'func-healthcare',    label: 'Healthcare / Clinical' },
  { id: 'func-education',     label: 'Education & Training' },
  { id: 'func-it',            label: 'IT & Infrastructure' },
  { id: 'func-creative',      label: 'Creative & Content' },
  { id: 'func-admin',         label: 'Administration & Office' },
  { id: 'func-pr-comms',      label: 'Public Relations & Communications' },
  { id: 'func-project-mgmt',  label: 'Project Management' },
  { id: 'func-qa',            label: 'Quality Assurance' },
  { id: 'func-legal',         label: 'Legal' },
  { id: 'func-rnd',           label: 'Research & Development' },
  { id: 'func-supply-chain',  label: 'Supply Chain & Logistics' },
  { id: 'func-procurement',   label: 'Procurement' },
  { id: 'func-real-estate',   label: 'Real Estate' },
  { id: 'func-govt',          label: 'Government & Public Sector' },
  { id: 'func-nonprofit',     label: 'Non-Profit / Social Impact' },
  { id: 'func-manufacturing', label: 'Manufacturing & Production' },
  { id: 'func-hw-eng',        label: 'Hardware Engineering' },
  { id: 'func-security',      label: 'Security & Risk' },
  { id: 'func-other',         label: 'Other' },
];

// ─── Industry sectors ──────────────────────────────────────────────────────────
const INDUSTRIES: DropdownOption[] = [
  { id: 'ind-tech',         label: 'Technology / Software' },
  { id: 'ind-healthcare',   label: 'Healthcare & Medical' },
  { id: 'ind-finance',      label: 'Finance & Banking' },
  { id: 'ind-education',    label: 'Education' },
  { id: 'ind-govt',         label: 'Government & Public Sector' },
  { id: 'ind-retail',       label: 'Retail & E-Commerce' },
  { id: 'ind-manufacturing',label: 'Manufacturing' },
  { id: 'ind-media',        label: 'Media & Entertainment' },
  { id: 'ind-nonprofit',    label: 'Non-Profit & NGO' },
  { id: 'ind-real-estate',  label: 'Real Estate' },
  { id: 'ind-consulting',   label: 'Consulting & Professional Services' },
  { id: 'ind-marketing',    label: 'Marketing & Advertising' },
  { id: 'ind-legal',        label: 'Legal Services' },
  { id: 'ind-construction', label: 'Construction & Engineering' },
  { id: 'ind-energy',       label: 'Energy & Utilities' },
  { id: 'ind-logistics',    label: 'Transportation & Logistics' },
  { id: 'ind-agriculture',  label: 'Agriculture & Food' },
  { id: 'ind-hospitality',  label: 'Hospitality & Tourism' },
  { id: 'ind-insurance',    label: 'Insurance' },
  { id: 'ind-pharma',       label: 'Pharmaceuticals & Biotech' },
  { id: 'ind-defense',      label: 'Defense & Aerospace' },
  { id: 'ind-telecom',      label: 'Telecommunications' },
  { id: 'ind-fashion',      label: 'Fashion & Apparel' },
  { id: 'ind-automotive',   label: 'Automotive' },
  { id: 'ind-sports',       label: 'Sports & Fitness' },
  { id: 'ind-consumer',     label: 'Consumer Goods' },
  { id: 'ind-architecture', label: 'Architecture & Design' },
  { id: 'ind-research',     label: 'Research & Academia' },
  { id: 'ind-startups',     label: 'Startups & Venture' },
  { id: 'ind-cybersecurity',label: 'Cybersecurity' },
  { id: 'ind-other',        label: 'Other' },
];

// ─── Job titles ────────────────────────────────────────────────────────────────
// Common job titles for "current job" and "actively exploring" questions.
// Users can also type anything freely (free-text is accepted).
const JOB_TITLES: DropdownOption[] = [
  { id: 'job-software-eng',     label: 'Software Engineer' },
  { id: 'job-data-analyst',     label: 'Data Analyst' },
  { id: 'job-data-scientist',   label: 'Data Scientist' },
  { id: 'job-ml-engineer',      label: 'Machine Learning Engineer' },
  { id: 'job-ai-engineer',      label: 'AI Engineer' },
  { id: 'job-product-mgr',      label: 'Product Manager' },
  { id: 'job-project-mgr',      label: 'Project Manager' },
  { id: 'job-full-stack',       label: 'Full Stack Developer' },
  { id: 'job-frontend-dev',     label: 'Front-End Developer' },
  { id: 'job-backend-dev',      label: 'Back-End Developer' },
  { id: 'job-devops',           label: 'DevOps / Cloud Engineer' },
  { id: 'job-qa-engineer',      label: 'QA / Test Engineer' },
  { id: 'job-cybersecurity',    label: 'Cybersecurity Analyst' },
  { id: 'job-network-eng',      label: 'Network Engineer' },
  { id: 'job-ux-designer',      label: 'UX / Product Designer' },
  { id: 'job-ux-researcher',    label: 'UX Researcher' },
  { id: 'job-graphic-designer', label: 'Graphic Designer' },
  { id: 'job-marketing-mgr',    label: 'Marketing Manager' },
  { id: 'job-content-writer',   label: 'Content Writer / Copywriter' },
  { id: 'job-social-media-mgr', label: 'Social Media Manager' },
  { id: 'job-brand-mgr',        label: 'Brand Manager' },
  { id: 'job-seo-specialist',   label: 'SEO Specialist' },
  { id: 'job-sales-rep',        label: 'Sales Representative' },
  { id: 'job-account-exec',     label: 'Account Executive' },
  { id: 'job-biz-dev-mgr',      label: 'Business Development Manager' },
  { id: 'job-vp-sales',         label: 'VP of Sales' },
  { id: 'job-cs-mgr',           label: 'Customer Success Manager' },
  { id: 'job-operations-mgr',   label: 'Operations Manager' },
  { id: 'job-dir-ops',          label: 'Director of Operations' },
  { id: 'job-supply-chain',     label: 'Supply Chain Manager' },
  { id: 'job-logistics',        label: 'Logistics Coordinator' },
  { id: 'job-scrum-master',     label: 'Scrum Master / Agile Coach' },
  { id: 'job-business-analyst', label: 'Business Analyst' },
  { id: 'job-strategy-analyst', label: 'Strategy Analyst' },
  { id: 'job-consultant',       label: 'Consultant' },
  { id: 'job-finance-mgr',      label: 'Finance Manager' },
  { id: 'job-financial-analyst',label: 'Financial Analyst' },
  { id: 'job-accountant',       label: 'Accountant' },
  { id: 'job-cfo',              label: 'CFO / VP of Finance' },
  { id: 'job-hr-generalist',    label: 'HR Generalist' },
  { id: 'job-recruiter',        label: 'Recruiter / Talent Acquisition' },
  { id: 'job-teacher',          label: 'Teacher / Educator' },
  { id: 'job-professor',        label: 'Professor / Academic' },
  { id: 'job-researcher',       label: 'Researcher / Scientist' },
  { id: 'job-nurse',            label: 'Registered Nurse (RN)' },
  { id: 'job-doctor',           label: 'Physician / Doctor' },
  { id: 'job-dentist',          label: 'Dentist' },
  { id: 'job-pharmacist',       label: 'Pharmacist' },
  { id: 'job-therapist',        label: 'Therapist / Counselor' },
  { id: 'job-social-worker',    label: 'Social Worker' },
  { id: 'job-lawyer',           label: 'Lawyer / Attorney' },
  { id: 'job-policy-analyst',   label: 'Policy Analyst' },
  { id: 'job-real-estate-agent',label: 'Real Estate Agent' },
  { id: 'job-architect',        label: 'Architect' },
  { id: 'job-civil-eng',        label: 'Civil Engineer' },
  { id: 'job-mech-eng',         label: 'Mechanical Engineer' },
  { id: 'job-elec-eng',         label: 'Electrical Engineer' },
  { id: 'job-biomed-eng',       label: 'Biomedical Engineer' },
  { id: 'job-journalist',       label: 'Journalist / Reporter' },
  { id: 'job-photographer',     label: 'Photographer / Videographer' },
  { id: 'job-admin-assistant',  label: 'Administrative Assistant' },
  { id: 'job-office-manager',   label: 'Office Manager' },
  { id: 'job-it-support',       label: 'IT Support / Help Desk' },
  { id: 'job-ceo',              label: 'CEO / Founder' },
  { id: 'job-cto',              label: 'CTO / VP of Engineering' },
  { id: 'job-cmo',              label: 'CMO / VP of Marketing' },
  { id: 'job-student',          label: 'Student' },
  { id: 'job-freelancer',       label: 'Freelancer / Self-Employed' },
  { id: 'job-unemployed',       label: 'Currently Unemployed / Job Seeking' },
  { id: 'job-other',            label: 'Other' },
];

/**
 * Map from question ID → the option list to render.
 * These replace the empty (or sentinel-only) options from the CSV manifest.
 */
export const DROPDOWN_OPTIONS: Record<string, DropdownOption[]> = {
  '383234': SCHOOL_MAJORS,          // "What was your school major?"

  '383018': JOB_TITLES,             // "What is your current job?"
  '383043': JOB_TITLES,             // "What job are you actively exploring?"

  '383080': JOB_FUNCTIONS,          // "Which job functions would you NOT want?" (undesiredFunction)
  '383104': JOB_FUNCTIONS,          // "Which job functions appeal to you?" (desiredFunction)

  '383091': INDUSTRIES,             // "What industry do you currently work in?" (employmentIndustry)
  '383095': INDUSTRIES,             // "Which industries interest you?" (desiredIndustry)
  '383107': INDUSTRIES,             // "Which industries would you NOT want?" (undesiredIndustry)
  '383125': INDUSTRIES,             // "Which industry would you NOT want?" (employmentIndustryUndesired)
};
